import React, { Component } from "react";
import XLSX from "xlsx";
import Swal from "sweetalert2/dist/sweetalert2.js";
import "sweetalert2/src/sweetalert2.scss";

import {
  KINDS,
  TABS,
  audit,
  classify,
  nf,
  parseCatalog,
  parseMaster,
  parseNis,
  parseSpec,
  plural,
  prettyLabel,
  summaryModel,
  tagFor,
} from "./goLiveAudit.engine";
import "./frmGoLiveAudit.css";

/* Go-live audit.
 *
 * Checks what was uploaded in NIS templates against what is actually live on
 * the catalogue, and says which products did not make it and why.
 *
 * Every spreadsheet is read in the browser with the app's own copy of SheetJS.
 * Nothing is uploaded and no request leaves the page, which is what lets an
 * unreleased catalogue be checked here at all.
 */

const PAGE_SIZE = 400;

class frmGoLiveAudit extends Component {
  state = {
    files: [],        // {name, kind, count, wb}
    report: null,
    activeTab: 0,
    search: {},       // per-tab filter text
    shown: {},        // per-tab row limit, for the "show more" button
    busy: false,
    status: null,     // {msg, kind}
    dragging: false,
  };

  // Parsed sources live outside state: they are large, never rendered directly,
  // and re-cloning them on every keystroke in the filter box would be wasteful.
  store = { spec: null, catalog: null, master: null, nis: [] };

  fileInput = React.createRef();

  /* A file dropped anywhere on the page is taken, not only one aimed at the
     dashed box. Dropping onto a browser window otherwise navigates away from
     the app and loses whatever has been loaded so far, which is a harsh
     punishment for missing a target. */
  componentDidMount() {
    document.addEventListener("dragover", this.onDocDragOver);
    document.addEventListener("dragleave", this.onDocDragLeave);
    document.addEventListener("drop", this.onDocDrop);
  }

  componentWillUnmount() {
    document.removeEventListener("dragover", this.onDocDragOver);
    document.removeEventListener("dragleave", this.onDocDragLeave);
    document.removeEventListener("drop", this.onDocDrop);
  }

  onDocDragOver = (e) => {
    e.preventDefault();
    if (!this.state.dragging) this.setState({ dragging: true });
  };

  onDocDragLeave = (e) => {
    // Leaving for a child element is not leaving the page.
    if (e.relatedTarget) return;
    this.setState({ dragging: false });
  };

  onDocDrop = (e) => {
    e.preventDefault();
    this.setState({ dragging: false });
    if (e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files.length)
      this.takeFiles(e.dataTransfer.files);
  };

  /* ------------------------------------------------------------- intake */

  setStatus = (msg, kind) => this.setState({ status: msg ? { msg, kind } : null });

  unregister(f) {
    if (f.kind === "spec") this.store.spec = null;
    if (f.kind === "catalog") this.store.catalog = null;
    if (f.kind === "master") this.store.master = null;
    if (f.kind === "nis")
      this.store.nis = this.store.nis.filter((n) => n.label !== prettyLabel(f.name));
  }

  register(f, kind) {
    let count = null;
    if (kind === "spec") {
      this.store.spec = parseSpec(f.wb);
      count = this.store.spec.length;
    } else if (kind === "catalog") {
      this.store.catalog = parseCatalog(f.wb);
      count = this.store.catalog.rows.length;
    } else if (kind === "master") {
      const m = parseMaster(f.wb, prettyLabel(f.name));
      if (!m) {
        f.kind = "unknown";
        f.count = null;
        return;
      }
      this.store.master = m;
      count = m.rows.length;
    } else if (kind === "nis") {
      const n = parseNis(f.wb, prettyLabel(f.name));
      this.store.nis = this.store.nis.filter((x) => x.label !== n.label).concat([n]);
      count = n.rows.length;
    }
    f.kind = kind;
    f.count = count;
  }

  takeFiles = async (list) => {
    if (!list || !list.length) return;
    this.setState({ busy: true });
    this.setStatus("Reading " + list.length + " " + plural(list.length, "file", "files") + "…");

    let files = this.state.files.slice();

    for (const file of Array.from(list)) {
      let wb = null;
      try {
        const buf = await file.arrayBuffer();
        wb = XLSX.read(new Uint8Array(buf), { type: "array" });
      } catch (e) {
        files.push({ name: file.name, kind: "unknown", count: null, wb: null });
        continue;
      }
      const kind = classify(wb);
      const entry = { name: file.name, kind: "unknown", count: null, wb };

      // Re-dropping a file replaces it, and a second file of a single-slot kind
      // replaces the first: there is only ever one spec, catalogue or master.
      files = files.filter((f) => {
        if (f.name === file.name) {
          this.unregister(f);
          return false;
        }
        if (kind !== "nis" && f.kind === kind) {
          this.unregister(f);
          return false;
        }
        return true;
      });

      try {
        if (kind !== "unknown") this.register(entry, kind);
      } catch (e) {
        entry.kind = "unknown";
      }
      files.push(entry);
    }

    this.setState({ files, busy: false, report: null });
    this.setStatus(null);
  };

  retype = (i, kind) => {
    const files = this.state.files.slice();
    const f = Object.assign({}, files[i]);
    this.unregister(f);
    try {
      this.register(f, kind);
    } catch (e) {
      f.kind = "unknown";
      f.count = null;
    }
    files[i] = f;
    this.setState({ files, report: null });
  };

  removeFile = (i) => {
    const files = this.state.files.slice();
    this.unregister(files[i]);
    files.splice(i, 1);
    this.setState({ files, report: null });
  };

  onDrop = (e) => {
    e.preventDefault();
    this.setState({ dragging: false });
    this.takeFiles(e.dataTransfer.files);
  };

  /* --------------------------------------------------------------- run */

  run = () => {
    const { spec, catalog, nis, master } = this.store;
    if (!spec || !catalog || !nis.length) return;

    this.setState({ busy: true });
    this.setStatus("Checking…");

    // Let the spinner paint before a synchronous pass over tens of thousands of
    // rows locks the main thread.
    setTimeout(() => {
      try {
        const report = audit(spec, catalog, nis, master);
        this.setState({ report, activeTab: 0, search: {}, shown: {}, busy: false });
        this.setStatus(
          nf(report.totals.liveBarcodes) +
            " of " +
            nf(report.totals.uploadedBarcodes) +
            " uploaded barcodes are live.",
          report.totals.heldBarcodes ? "bad" : "good"
        );
      } catch (err) {
        this.setState({ busy: false });
        this.setStatus(
          "The files could not be read together: " +
            (err && err.message ? err.message : String(err)) +
            ". Check that the catalogue export and the mandatory sheet match the templates.",
          "bad"
        );
      }
    }, 30);
  };

  /* ------------------------------------------------------------ export */

  exportReport = async () => {
    const { report } = this.state;
    if (!report) return;
    this.setState({ busy: true });
    this.setStatus("Building the workbook…");

    try {
      // Loaded on demand: ExcelJS is large and only the download needs it, so
      // it stays out of the main bundle and off the page's first paint.
      const mod = await import("exceljs");
      const ExcelJS = mod.default || mod;
      const wb = new ExcelJS.Workbook();
      const stamp = new Date().toISOString().slice(0, 10);

      const HEAD = "FF1F3864";
      const RULE = "FFBFBFBF";
      const font = (o) => Object.assign({ name: "Arial", size: 10 }, o || {});
      const fill = (cell, argb) =>
        (cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb } });
      const border = (cell) => {
        const e = { style: "thin", color: { argb: RULE } };
        cell.border = { top: e, left: e, bottom: e, right: e };
      };

      /* Summary sheet — the same model the Overview tab draws. */
      const ws = wb.addWorksheet("Summary", { views: [{ showGridLines: false }] });
      ws.getColumn(1).width = 58;
      ws.getColumn(2).width = 46;
      ws.getColumn(3).width = 16;

      let r = ws.addRow(["Go-live completeness check"]);
      r.getCell(1).font = font({ size: 14, bold: true });
      ws.mergeCells(r.number, 1, r.number, 3);
      r = ws.addRow([
        "NIS uploads vs the live catalogue export, checked against the mandatory-attribute sheet. Generated " +
          stamp +
          ".",
      ]);
      r.getCell(1).font = font({ italic: true, color: { argb: "FF767F84" } });
      ws.mergeCells(r.number, 1, r.number, 3);
      ws.addRow([]);

      summaryModel(report).forEach((m) => {
        if (m.k === "gap") return void ws.addRow([]);
        if (m.k === "head") {
          const row = ws.addRow([m.l, m.cols ? "Products" : "", m.cols ? "Barcodes" : ""]);
          for (let i = 1; i <= 3; i++) {
            const c = row.getCell(i);
            fill(c, HEAD);
            c.font = font({ bold: true, color: { argb: "FFFFFFFF" } });
            c.alignment = { vertical: "middle", horizontal: i === 1 ? "left" : "right" };
            border(c);
          }
          row.height = 19;
          return;
        }
        if (m.k === "note") {
          const row = ws.addRow([m.l]);
          row.getCell(1).font = font({ italic: true, color: { argb: "FF767F84" } });
          row.getCell(1).alignment = { wrapText: true, vertical: "top" };
          ws.mergeCells(row.number, 1, row.number, 3);
          return;
        }
        const row = ws.addRow([
          String(m.l).replace(/ /g, "  "),
          m.p === "" || m.p == null ? "" : m.p,
          m.b === "" || m.b == null ? "" : m.b,
        ]);
        for (let i = 1; i <= 3; i++) {
          const c = row.getCell(i);
          c.font = font();
          c.alignment = {
            wrapText: i !== 3,
            vertical: "top",
            horizontal: i === 1 ? "left" : typeof c.value === "number" ? "right" : "left",
          };
          border(c);
        }
        fill(row.getCell(1), "FFF7F8F5");
      });

      /* One sheet per tab that has rows. */
      TABS.filter((t) => t.get && (!t.when || t.when(report))).forEach((tab) => {
        const rows = tab.get(report) || [];
        if (!rows.length) return;
        const name = tab.label.slice(0, 31).replace(/[\\/?*[\]:]/g, " ");
        const sh = wb.addWorksheet(name, {
          views: [{ state: "frozen", ySplit: 1, showGridLines: false }],
        });
        const cols = Object.keys(rows[0]);
        const wrapSet = new Set(tab.wrap || []);

        const head = sh.addRow(cols);
        cols.forEach((c, i) => {
          const cell = head.getCell(i + 1);
          fill(cell, HEAD);
          cell.font = font({ bold: true, color: { argb: "FFFFFFFF" } });
          cell.alignment = { vertical: "middle", wrapText: true };
          border(cell);
        });
        head.height = 20;

        rows.forEach((d) => {
          const row = sh.addRow(
            cols.map((c) => {
              const v = d[c];
              return typeof v === "number" ? v : v == null ? "" : String(v);
            })
          );
          // Severity, where present, decides the tint instead of the tab colour:
          // a High row should read as urgent wherever it appears.
          let tint = tab.tint;
          if (cols.includes("Severity")) {
            const sev = String(d.Severity || "").toLowerCase();
            tint = sev === "high" ? "FFFCE4E4" : sev === "check" ? "FFFFF2CC" : "FFE2EFDA";
          }
          cols.forEach((c, i) => {
            const cell = row.getCell(i + 1);
            cell.font = font();
            cell.alignment = { vertical: "top", wrapText: wrapSet.has(c) };
            border(cell);
            if (tint) fill(cell, tint);
          });
        });

        cols.forEach((c, i) => {
          if (wrapSet.has(c)) {
            sh.getColumn(i + 1).width = 58;
            return;
          }
          let w = c.length + 3;
          for (let j = 0; j < Math.min(rows.length, 400); j++) {
            const v = rows[j][c];
            if (v != null) w = Math.max(w, String(v).length + 2);
          }
          sh.getColumn(i + 1).width = Math.min(46, Math.max(10, w));
        });
        sh.autoFilter = {
          from: { row: 1, column: 1 },
          to: { row: rows.length + 1, column: cols.length },
        };
      });

      const buf = await wb.xlsx.writeBuffer();
      const blob = new Blob([buf], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const name = "go-live-audit-" + stamp + ".xlsx";
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);

      this.setState({ busy: false });
      this.setStatus(
        "Saved as " + name + " (" + Math.round(blob.size / 1024) + " KB). Check your downloads folder.",
        "good"
      );
    } catch (err) {
      this.setState({ busy: false });
      this.setStatus(
        "The file could not be saved: " +
          (err && err.message ? err.message : String(err)) +
          "\n\nUse Copy table instead.",
        "bad"
      );
    }
  };

  copyCurrent = () => {
    const { report, activeTab } = this.state;
    if (!report) return;
    let tab = TABS[activeTab];
    if (tab.when && !tab.when(report)) tab = TABS[0];

    // Built as a grid, then joined with tabs, so it pastes straight into a
    // spreadsheet with the columns already lined up.
    let rows;
    if (tab.id === "overview") {
      rows = summaryModel(report).map((m) =>
        m.k === "row"
          ? [
              String(m.l).replace(/ /g, ""),
              m.p === "" || m.p == null ? "" : m.p,
              m.b === "" || m.b == null ? "" : m.b,
            ]
          : [m.l == null ? "" : m.l, "", ""]
      );
      rows.unshift(["Check", "Products", "Barcodes"]);
    } else {
      const data = tab.get(report) || [];
      if (!data.length) {
        Swal.fire({ icon: "info", title: "Nothing to copy here.", timer: 1400, showConfirmButton: false });
        return;
      }
      const cols = Object.keys(data[0]);
      rows = [cols].concat(
        data.map((d) => cols.map((c) => String(d[c] == null ? "" : d[c])))
      );
    }

    // A tab or newline inside a cell would break the grid it is pasted into.
    const tsv = rows
      .map((r) => r.map((v) => String(v).replace(/[\t\r\n]+/g, " ")).join("\t"))
      .join("\n");

    const n = rows.length - 1;
    const done = () =>
      Swal.fire({
        icon: "success",
        title: "Copied " + nf(n) + " " + plural(n, "row", "rows"),
        text: "Paste straight into a sheet.",
        timer: 1600,
        showConfirmButton: false,
      });
    const fallback = () => {
      const ta = document.createElement("textarea");
      ta.value = tsv;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand("copy");
        done();
      } catch (e) {
        Swal.fire({ icon: "error", title: "Copying is blocked in this view." });
      }
      ta.remove();
    };

    if (navigator.clipboard && navigator.clipboard.writeText)
      navigator.clipboard.writeText(tsv).then(done, fallback);
    else fallback();
  };

  /* ------------------------------------------------------------ render */

  renderSlots() {
    const { files } = this.state;
    const { spec, catalog, master, nis } = this.store;

    const waiting = (t, sub) => (
      <div className="gla-slot" key={"w" + t}>
        <div className="dot" />
        <div className="sw">
          <div className="t">{t}</div>
          <div className="s">{sub}</div>
        </div>
      </div>
    );

    return (
      <div className="gla-slots">
        {files.map((f, i) => (
          <div className={"gla-slot " + (f.kind === "unknown" ? "bad" : "ok")} key={f.name + i}>
            <div className="dot" />
            <div className="sw">
              <div className="t">{KINDS[f.kind]}</div>
              <div className="s">
                {f.name}
                {f.count != null ? "  ·  " + nf(f.count) + " rows" : ""}
              </div>
              <select
                className="gla-retype"
                value={f.kind}
                onChange={(e) => this.retype(i, e.target.value)}
              >
                {Object.keys(KINDS).map((k) =>
                  k === "unknown" && f.kind !== "unknown" ? null : (
                    <option key={k} value={k}>
                      Read as: {KINDS[k]}
                    </option>
                  )
                )}
              </select>
            </div>
            <button className="x" title="Remove" onClick={() => this.removeFile(i)}>
              ×
            </button>
          </div>
        ))}
        {!spec && waiting("Waiting: mandatory attributes", "the sheet listing mandatory vs optional")}
        {!catalog && waiting("Waiting: live catalogue export", "full_catalog_export…")}
        {!nis.length && waiting("Waiting: NIS templates", "one or more filled templates")}
        {!master && waiting("Optional: master list", "every barcode that is meant to go live")}
      </div>
    );
  }

  renderIntro() {
    const steps = [
      ["01", "Mandatory attributes", "the sheet that marks each attribute mandatory or optional and names its column in the catalogue export"],
      ["02", "Live catalogue export", "the full export of what is currently live"],
      ["03", "NIS templates", "one or more filled templates — shoes, bags, anything. Add as many as you like"],
      ["04", "Master list (optional)", "every barcode that is meant to go live, so the check can also show what was never templated"],
    ];
    return (
      <div className="gla-intro gla-reveal">
        <h2>
          <span className="thin">Is everything</span>actually live?
        </h2>
        <p>
          Add the spreadsheets on the left and run the check. Nothing is uploaded anywhere — the
          files are read in this browser tab.
        </p>
        <ul className="gla-check">
          {steps.map(([n, t, s]) => (
            <li key={n}>
              <span className="n">{n}</span>
              <span>
                <b>{t}</b> {" — " + s}
              </span>
            </li>
          ))}
        </ul>
      </div>
    );
  }

  renderOverview(r) {
    const t = r.totals;
    const stat = (cls, v, l, sub) => (
      <div className={"gla-stat " + cls} key={l}>
        <div className="v">{nf(v)}</div>
        <div className="l">{l}</div>
        {sub ? <div className="sub">{sub}</div> : null}
      </div>
    );

    return (
      <div>
        <div className="gla-band gla-reveal">
          {stat("", t.uploadedBarcodes, "Barcodes uploaded", nf(t.uploadedProducts) + " products")}
          {stat("clear", t.liveBarcodes, "Live on the catalogue", nf(t.liveProducts) + " products")}
          {stat("hold", t.heldBarcodes, "Uploaded but not live", nf(t.heldProducts) + " products")}
          {stat(
            "defect",
            r.mandatoryGaps.reduce((a, b) => a + b.Barcodes, 0),
            "Mandatory fields blank on live",
            nf(r.mandatoryGaps.reduce((a, b) => a + b.Products, 0)) + " products"
          )}
          {r.masterStats
            ? stat(
                "hold",
                r.masterStats.neverTemplated,
                "Never filled into a template",
                "of " + nf(r.masterStats.total) + " on the master list"
              )
            : null}
        </div>

        <div className="gla-panel gla-reveal">
          <div className="gla-phead">
            <div>
              <h3>Go-live completeness check</h3>
              <p>
                NIS uploads vs the live catalogue export, checked against the mandatory-attribute
                sheet.
              </p>
            </div>
            <div className="gla-tools">
              <button className="btn btn-sm btn-default" onClick={this.copyCurrent}>
                Copy summary
              </button>
            </div>
          </div>
          <div className="gla-scroll">
            <table>
              <tbody>
                {summaryModel(r).map((m, i) => {
                  if (m.k === "gap")
                    return (
                      <tr key={i}>
                        <td className="gla-spacer" colSpan={3} />
                      </tr>
                    );
                  if (m.k === "head")
                    return (
                      <tr key={i}>
                        <td className="gla-sechead">{m.l}</td>
                        <td className="gla-sechead mono">{m.cols ? "Products" : ""}</td>
                        <td className="gla-sechead mono">{m.cols ? "Barcodes" : ""}</td>
                      </tr>
                    );
                  if (m.k === "note")
                    return (
                      <tr key={i}>
                        <td className="gla-notecell" colSpan={3}>
                          {m.l}
                        </td>
                      </tr>
                    );
                  const isNum = (v) => v !== "" && v != null && typeof v === "number";
                  const cell = (v, j) => (
                    <td key={j} className={isNum(v) ? "mono" : "gla-valcell"}>
                      {v === "" || v == null ? "" : isNum(v) ? nf(v) : String(v)}
                    </td>
                  );
                  return (
                    <tr key={i}>
                      <td>{m.l}</td>
                      {cell(m.p, "p")}
                      {cell(m.b, "b")}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  }

  renderTable(tab, rows) {
    const term = (this.state.search[tab.id] || "").trim().toLowerCase();
    const limit = this.state.shown[tab.id] || PAGE_SIZE;

    const filtered = term
      ? rows.filter((d) =>
          Object.keys(d).some((c) => String(d[c] == null ? "" : d[c]).toLowerCase().includes(term))
        )
      : rows;

    const cols = rows.length ? Object.keys(rows[0]) : [];
    const wrapSet = new Set(tab.wrap || []);
    const page = filtered.slice(0, limit);

    return (
      <div className="gla-panel gla-reveal">
        <div className="gla-phead">
          <div>
            <h3>{tab.label}</h3>
            <p>{tab.blurb}</p>
          </div>
          <div className="gla-tools">
            <input
              type="search"
              placeholder="Filter…"
              value={this.state.search[tab.id] || ""}
              onChange={(e) =>
                this.setState({
                  search: Object.assign({}, this.state.search, { [tab.id]: e.target.value }),
                  shown: Object.assign({}, this.state.shown, { [tab.id]: PAGE_SIZE }),
                })
              }
            />
            <button className="btn btn-sm btn-default" onClick={this.copyCurrent}>
              Copy table
            </button>
          </div>
        </div>

        {!filtered.length ? (
          <div className="gla-empty">
            {rows.length ? "Nothing matches your filter." : "Nothing to report here."}
          </div>
        ) : (
          <div className="gla-scroll">
            <table>
              <thead>
                <tr>
                  {cols.map((c) => (
                    <th key={c} className={wrapSet.has(c) ? "" : "mono"}>
                      {c}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {page.map((d, i) => (
                  <tr key={i}>
                    {cols.map((c) => {
                      const v = d[c] == null ? "" : d[c];
                      const tg = tagFor(v);
                      return (
                        <td
                          key={c}
                          className={
                            wrapSet.has(c) ? "wrap" : typeof v === "number" ? "mono" : ""
                          }
                        >
                          {tg ? (
                            <span className={"gla-tag " + tg}>{String(v)}</span>
                          ) : typeof v === "number" ? (
                            nf(v)
                          ) : (
                            String(v)
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {filtered.length > limit ? (
          <div className="gla-more">
            <button
              className="btn btn-sm btn-default"
              onClick={() =>
                this.setState({
                  shown: Object.assign({}, this.state.shown, { [tab.id]: limit + PAGE_SIZE }),
                })
              }
            >
              Show {nf(Math.min(PAGE_SIZE, filtered.length - limit))} more of {nf(filtered.length)}
            </button>
          </div>
        ) : (
          <div className="gla-note">
            {nf(filtered.length)} {plural(filtered.length, "row", "rows")}
            {term ? " matching your filter" : ""}.
          </div>
        )}
      </div>
    );
  }

  render() {
    const { report, busy, status, dragging } = this.state;
    const { spec, catalog, nis } = this.store;
    const canRun = !!(spec && catalog && nis.length) && !busy;

    let activeTab = this.state.activeTab;
    let tab = TABS[activeTab];
    if (report && tab && tab.when && !tab.when(report)) {
      activeTab = 0;
      tab = TABS[0];
    }

    return (
      <div className="content-wrapper">
        <section className="content">
          <div className="box box-success gla">
            <div
              className="box-header with-border"
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                padding: "12px 20px",
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 11, color: "#aaa", marginBottom: 3 }}>
                  Dashboard &gt; Image Tools &gt; Go-Live Audit
                </div>
                <h3
                  className="box-title"
                  style={{ margin: 0, fontSize: 18, fontWeight: 600 }}
                >
                  Go-Live Audit
                </h3>
              </div>
            </div>

            <div className="box-body">
              <div className="gla-app">
                {/* ---------------- intake rail ---------------- */}
                <aside className="gla-rail">
                  <div
                    className={"gla-drop" + (dragging ? " over" : "")}
                    role="button"
                    tabIndex={0}
                    onClick={() => this.fileInput.current && this.fileInput.current.click()}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        if (this.fileInput.current) this.fileInput.current.click();
                      }
                    }}
                    onDragOver={(e) => {
                      e.preventDefault();
                      if (!dragging) this.setState({ dragging: true });
                    }}
                    onDrop={this.onDrop}
                  >
                    <strong>Drop spreadsheets here</strong>
                    <span>or click to choose · .xlsx</span>
                    <input
                      ref={this.fileInput}
                      type="file"
                      multiple
                      accept=".xlsx,.xlsm,.xls"
                      hidden
                      onChange={(e) => {
                        this.takeFiles(e.target.files);
                        e.target.value = ""; // so the same file can be re-added
                      }}
                    />
                  </div>

                  {this.renderSlots()}

                  {/* The actions sit under the files they act on, so the whole
                      sequence — add, check, download — reads top to bottom in
                      one column. */}
                  <button
                    className="btn btn-success gla-run"
                    onClick={this.run}
                    disabled={!canRun}
                  >
                    {busy ? "Working…" : "Run the check"}
                  </button>

                  {report ? (
                    <button
                      className="btn btn-default gla-run"
                      onClick={this.exportReport}
                      disabled={busy}
                    >
                      ⇩ Download the report
                    </button>
                  ) : null}

                  {status ? (
                    <div className={"gla-state" + (status.kind ? " " + status.kind : "")}>
                      {status.msg}
                    </div>
                  ) : null}

                  <div className="gla-railfoot">
                    Needs the mandatory-attribute sheet, the live catalogue export and one or more
                    filled NIS templates. Add the master list of barcodes to also see what was never
                    templated. Everything stays in your browser.
                  </div>
                </aside>

                {/* ---------------- main ---------------- */}
                <div className="gla-main">
                  {!report ? (
                    this.renderIntro()
                  ) : (
                    <div>
                      <div className="gla-tabs">
                        {TABS.map((t, i) => {
                          if (t.when && !t.when(report)) return null;
                          const n = t.id === "overview" ? null : (t.get(report) || []).length;
                          return (
                            <button
                              key={t.id}
                              className="gla-tab"
                              role="tab"
                              aria-selected={i === activeTab ? "true" : "false"}
                              onClick={() => this.setState({ activeTab: i })}
                            >
                              <span>{t.label}</span>
                              {n != null ? <span className="c">{nf(n)}</span> : null}
                            </button>
                          );
                        })}
                      </div>
                      {tab.id === "overview"
                        ? this.renderOverview(report)
                        : this.renderTable(tab, tab.get(report) || [])}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    );
  }
}

export default frmGoLiveAudit;
